// LightingStage.tsx — real Three.js head-bust scene.
// Ported from design/lighting-stage.jsx. Exposes the same public API:
//   <LightingStage modelId lights rotation ambient exposure material />
// All visual feedback is driven by an actual WebGL scene with:
//   • a procedurally-shaped 3D head bust (4 variants — planar, écorché,
//     classical, skull) carved out of an icosahedron with vertex
//     displacement so it actually reads as a head.
//   • a pool of real THREE.SpotLights mapped 1:1 from the React `lights`
//     array — the 2D orbit (x,y ∈ [-1,1]) projects onto a hemisphere
//     around the head.
//   • ambient + tone-mapped exposure controls.

import { useEffect, useRef } from 'react';
import * as THREE from 'three';

// ─── Types ───────────────────────────────────────────────────────
export interface LightSpec {
  id: string;
  name?: string;
  x: number;
  y: number;
  intensity: number; // 0..100
  color?: string;
  on: boolean;
}

export type ModelId = 'planar' | 'ecorche' | 'classical' | 'skull';

export interface MaterialSpec {
  id: string;
  name: string;
  sub: string;
  native?: boolean;
  color?: string;
  roughness?: number;
  metalness?: number;
}

interface SkinUserData {
  skin?: boolean;
  native?: { color: number; roughness: number; metalness: number };
}

// ─── Defaults ────────────────────────────────────────────────────
export const DEFAULT_LIGHTS: LightSpec[] = [
  { id: 'key', name: 'Key', x: -0.55, y: -0.25, intensity: 85, color: '#ffeed1', on: true },
  { id: 'fill', name: 'Fill', x: 0.65, y: 0.1, intensity: 35, color: '#9ec5ff', on: true },
  { id: 'rim', name: 'Rim', x: 0.1, y: -0.7, intensity: 55, color: '#ffffff', on: true },
];

// Surface finishes applied on top of any head model. 'native' keeps each
// model's own baked material; all others override color/roughness/metalness
// on the skin meshes (head, neck, bust) only — leaving sockets, teeth and
// the écorché wireframe intact.
export const MATERIALS: MaterialSpec[] = [
  { id: 'native', name: 'Native', sub: 'Model default', native: true },
  { id: 'plaster', name: 'Plaster', sub: 'Matte cast', color: '#e9e7e1', roughness: 0.92, metalness: 0.0 },
  { id: 'clay', name: 'Clay', sub: "Sculptor's grey", color: '#9b9285', roughness: 0.85, metalness: 0.0 },
  { id: 'marble', name: 'Marble', sub: 'Carrara', color: '#ece9e1', roughness: 0.42, metalness: 0.0 },
  { id: 'porcelain', name: 'Porcelain', sub: 'Glazed white', color: '#f4f2ee', roughness: 0.12, metalness: 0.0 },
  { id: 'terracotta', name: 'Terracotta', sub: 'Fired earth', color: '#b16a49', roughness: 0.78, metalness: 0.0 },
  { id: 'wax', name: 'Wax', sub: 'Warm cast', color: '#e6c89c', roughness: 0.36, metalness: 0.0 },
  { id: 'bronze', name: 'Bronze', sub: 'Cast metal', color: '#b27d3f', roughness: 0.3, metalness: 0.95 },
  { id: 'graphite', name: 'Graphite', sub: 'Pencil grey', color: '#3b3b40', roughness: 0.55, metalness: 0.4 },
  { id: 'jade', name: 'Jade', sub: 'Polished stone', color: '#4f8a6c', roughness: 0.2, metalness: 0.1 },
  { id: 'obsidian', name: 'Obsidian', sub: 'Black glass', color: '#101015', roughness: 0.07, metalness: 0.25 },
  { id: 'copper', name: 'Copper', sub: 'Oxidised', color: '#a85a3c', roughness: 0.42, metalness: 0.9 },
];

export const DEFAULT_MATERIAL = MATERIALS[0]!;

// ─── Geometry helpers ────────────────────────────────────────────
// Carve an icosahedron into a head-ish bust.
function buildHeadGeometry(detail: number): THREE.IcosahedronGeometry {
  const geo = new THREE.IcosahedronGeometry(1, detail);
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    let x = pos.getX(i);
    let y = pos.getY(i);
    let z = pos.getZ(i);

    // Stretch vertically into an egg.
    y *= 1.28;
    // Squeeze sides — heads are narrower than they are tall.
    x *= 0.86;

    // Forehead / cranium: pull back the top-front.
    if (y > 0.55 && z > 0) z *= 0.92;
    // Brow ridge: a small push out around eye level.
    if (y > 0.05 && y < 0.35 && z > 0.55) z += 0.04;
    // Cheeks: flatten the sides at mid-face.
    if (y > -0.35 && y < 0.25 && Math.abs(x) > 0.55) x *= 0.92;
    // Chin: pull the lower-front forward + tuck it under.
    if (y < -0.25 && z > 0.25) {
      const t = THREE.MathUtils.clamp((-y - 0.25) / 0.6, 0, 1);
      z += t * 0.18;
      y -= t * 0.08;
    }
    // Jawline taper at the very bottom.
    if (y < -0.7) {
      x *= 0.78;
      z *= 0.85;
    }
    // Crown: gently round the top.
    if (y > 0.95) {
      x *= 0.78;
      z *= 0.78;
    }

    pos.setXYZ(i, x, y, z);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

// Build a small spherical concavity by pressing nearby vertices inward.
function pressConcavity(
  geo: THREE.BufferGeometry,
  cx: number,
  cy: number,
  cz: number,
  radius: number,
  depth: number,
): void {
  const pos = geo.attributes.position;
  const c = new THREE.Vector3(cx, cy, cz);
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.set(pos.getX(i), pos.getY(i), pos.getZ(i));
    const d = v.distanceTo(c);
    if (d < radius) {
      const t = 1 - d / radius;
      const dir = v.clone().normalize();
      v.addScaledVector(dir, -depth * t * t);
      pos.setXYZ(i, v.x, v.y, v.z);
    }
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
}

// ─── Head builder ─────────────────────────────────────────────────
function buildHead(modelId: ModelId): THREE.Group {
  const group = new THREE.Group();

  let detail: number;
  let color: number;
  let flat: boolean;
  let roughness: number;
  let metalness: number;
  switch (modelId) {
    case 'planar':
      detail = 1;
      color = 0xcfd1d6;
      flat = true;
      roughness = 0.85;
      metalness = 0.0;
      break;
    case 'ecorche':
      detail = 3;
      color = 0x8e3a3a;
      flat = true;
      roughness = 0.55;
      metalness = 0.05;
      break;
    case 'classical':
      detail = 5;
      color = 0xe8e2d3;
      flat = false;
      roughness = 0.65;
      metalness = 0.0;
      break;
    case 'skull':
      detail = 4;
      color = 0xddd4be;
      flat = false;
      roughness = 0.55;
      metalness = 0.0;
      break;
  }

  const geo = buildHeadGeometry(detail);

  if (modelId === 'skull') {
    pressConcavity(geo, -0.32, 0.1, 0.85, 0.34, 0.28);
    pressConcavity(geo, 0.32, 0.1, 0.85, 0.34, 0.28);
    pressConcavity(geo, 0.0, -0.18, 0.95, 0.22, 0.32);
    geo.computeVertexNormals();
  }

  const headMat = new THREE.MeshStandardMaterial({
    color,
    roughness,
    metalness,
    flatShading: flat,
  });
  const head = new THREE.Mesh(geo, headMat);
  head.castShadow = true;
  head.receiveShadow = true;
  (head.userData as SkinUserData).skin = true;
  (head.userData as SkinUserData).native = { color, roughness, metalness };
  group.add(head);

  if (modelId === 'ecorche') {
    const wire = new THREE.LineSegments(
      new THREE.WireframeGeometry(geo),
      new THREE.LineBasicMaterial({ color: 0x2a0a0a, transparent: true, opacity: 0.35 }),
    );
    group.add(wire);
  }

  if (modelId === 'skull') {
    const voidMat = new THREE.MeshBasicMaterial({ color: 0x050505 });
    const eyeGeo = new THREE.SphereGeometry(0.18, 18, 18);
    const noseGeo = new THREE.SphereGeometry(0.14, 14, 14);
    const lEye = new THREE.Mesh(eyeGeo, voidMat);
    lEye.position.set(-0.3, 0.08, 0.62);
    lEye.scale.set(1, 0.85, 0.6);
    const rEye = new THREE.Mesh(eyeGeo, voidMat);
    rEye.position.set(0.3, 0.08, 0.62);
    rEye.scale.set(1, 0.85, 0.6);
    const nose = new THREE.Mesh(noseGeo, voidMat);
    nose.position.set(0, -0.18, 0.72);
    nose.scale.set(0.7, 1.3, 0.5);
    group.add(lEye, rEye, nose);

    const teethMat = new THREE.MeshStandardMaterial({ color: 0x2a261d, roughness: 0.9 });
    for (let i = 0; i < 6; i++) {
      const t = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.08, 0.02), teethMat);
      t.position.set(-0.15 + i * 0.06, -0.55, 0.74);
      group.add(t);
    }
  }

  // Neck — tapered cylinder seated under the chin.
  const neckMat = headMat.clone();
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.36, 0.5, 0.55, 28, 1, false), neckMat);
  neck.position.set(0, -1.3, 0.02);
  neck.castShadow = true;
  neck.receiveShadow = true;
  (neck.userData as SkinUserData).skin = true;
  (neck.userData as SkinUserData).native = { color, roughness, metalness };
  group.add(neck);

  // Bust / shoulders.
  const bustMat = headMat.clone();
  const bust = new THREE.Mesh(new THREE.CylinderGeometry(0.85, 0.95, 0.55, 32, 1, false), bustMat);
  bust.scale.set(1.4, 1, 0.85);
  bust.position.set(0, -1.78, 0);
  bust.castShadow = true;
  bust.receiveShadow = true;
  (bust.userData as SkinUserData).skin = true;
  (bust.userData as SkinUserData).native = { color, roughness, metalness };
  group.add(bust);

  group.position.y = 0.35;
  return group;
}

// Map a 2D orbit position onto a 3D hemisphere around the head.
function lightPositionFor(x: number, y: number, radius = 4.0): THREE.Vector3 {
  const az = x * Math.PI * 0.55;
  const el = -y * Math.PI * 0.5;
  const cosE = Math.cos(el);
  const sinE = Math.sin(el);
  return new THREE.Vector3(
    radius * cosE * Math.sin(az),
    radius * sinE + 0.35,
    radius * cosE * Math.cos(az),
  );
}

// ─── Internal scene state ────────────────────────────────────────
interface StageState {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  ambientLight: THREE.AmbientLight;
  hemi: THREE.HemisphereLight;
  floor: THREE.Mesh;
  cyc: THREE.Mesh;
  headGroup: THREE.Group;
  lightsMap: Map<string, { light: THREE.SpotLight }>;
  currentModel: ModelId | null;
}

// ─── Props ───────────────────────────────────────────────────────
export interface LightingStageProps {
  modelId: ModelId;
  lights: LightSpec[];
  material?: MaterialSpec;
  rotation?: number; // degrees
  ambient?: number;  // 0..100
  exposure?: number; // 0..100
}

// ─── Component ───────────────────────────────────────────────────
export function LightingStage({
  modelId,
  lights,
  material = DEFAULT_MATERIAL,
  rotation = 0,
  ambient = 18,
  exposure = 0,
}: LightingStageProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef<StageState | null>(null);

  // Mount: build scene + renderer once.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const w = container.clientWidth;
    const h = container.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x07080a);

    const camera = new THREE.PerspectiveCamera(32, w / Math.max(h, 1), 0.1, 100);
    camera.position.set(0, 0.45, 5.2);
    camera.lookAt(0, 0.35, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(w, h, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(renderer.domElement);
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';

    const cycMat = new THREE.MeshStandardMaterial({ color: 0x121317, roughness: 1.0, metalness: 0.0 });
    const cyc = new THREE.Mesh(new THREE.PlaneGeometry(40, 30), cycMat);
    cyc.position.set(0, 0, -6);
    cyc.receiveShadow = true;
    scene.add(cyc);

    const floorMat = new THREE.MeshStandardMaterial({ color: 0x0d0e10, roughness: 0.95, metalness: 0.0 });
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(40, 40), floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -1.95;
    floor.receiveShadow = true;
    scene.add(floor);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.12);
    scene.add(ambientLight);
    const hemi = new THREE.HemisphereLight(0x3a4252, 0x0a0a0e, 0.15);
    scene.add(hemi);

    const headGroup = new THREE.Group();
    scene.add(headGroup);

    const lightsMap = new Map<string, { light: THREE.SpotLight }>();

    let raf = 0;
    const render = () => {
      raf = requestAnimationFrame(render);
      renderer.render(scene, camera);
    };
    render();

    const onResize = () => {
      const W = container.clientWidth;
      const H = container.clientHeight;
      if (W === 0 || H === 0) return;
      camera.aspect = W / H;
      camera.updateProjectionMatrix();
      renderer.setSize(W, H, false);
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(container);

    stateRef.current = {
      scene,
      camera,
      renderer,
      ambientLight,
      hemi,
      floor,
      cyc,
      headGroup,
      lightsMap,
      currentModel: null,
    };

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      headGroup.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose?.();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) mesh.material.forEach((m) => m.dispose?.());
          else mesh.material.dispose?.();
        }
      });
      floor.geometry.dispose();
      (floor.material as THREE.Material).dispose();
      cyc.geometry.dispose();
      (cyc.material as THREE.Material).dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  // Swap head model when modelId changes.
  useEffect(() => {
    const st = stateRef.current;
    if (!st) return;
    if (st.currentModel === modelId) return;
    while (st.headGroup.children.length) {
      const c = st.headGroup.children[0]!;
      st.headGroup.remove(c);
      c.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose?.();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) mesh.material.forEach((m) => m.dispose?.());
          else mesh.material.dispose?.();
        }
      });
    }
    const head = buildHead(modelId);
    st.headGroup.add(head);
    st.currentModel = modelId;
  }, [modelId]);

  // Apply surface material to the skin meshes. Runs after a model swap too
  // (shared modelId dep) so a chosen finish survives changing the head.
  useEffect(() => {
    const st = stateRef.current;
    if (!st) return;
    st.headGroup.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      const ud = mesh.userData as SkinUserData;
      if (!(mesh as THREE.Mesh).isMesh || !ud.skin || !mesh.material) return;
      const mat = mesh.material as THREE.MeshStandardMaterial;
      if (!material || material.native) {
        const n = ud.native;
        if (n) {
          mat.color.set(n.color);
          mat.roughness = n.roughness;
          mat.metalness = n.metalness;
        }
      } else {
        if (material.color !== undefined) mat.color.set(material.color);
        if (material.roughness !== undefined) mat.roughness = material.roughness;
        if (material.metalness !== undefined) mat.metalness = material.metalness;
      }
      mat.needsUpdate = true;
    });
  }, [material, modelId]);

  // Apply rotation (pose.turn — degrees).
  useEffect(() => {
    const st = stateRef.current;
    if (!st) return;
    st.headGroup.rotation.y = THREE.MathUtils.degToRad(rotation);
  }, [rotation]);

  // Sync the light pool to the props array.
  useEffect(() => {
    const st = stateRef.current;
    if (!st) return;
    const { scene, lightsMap } = st;

    for (const id of [...lightsMap.keys()]) {
      if (!lights.find((l) => l.id === id)) {
        const e = lightsMap.get(id);
        if (!e) continue;
        scene.remove(e.light);
        scene.remove(e.light.target);
        e.light.dispose?.();
        lightsMap.delete(id);
      }
    }

    for (const l of lights) {
      let entry = lightsMap.get(l.id);
      if (!entry) {
        const spot = new THREE.SpotLight(0xffffff, 0, 30, Math.PI / 3.5, 0.55, 1.2);
        spot.castShadow = true;
        spot.shadow.mapSize.set(512, 512);
        spot.shadow.bias = -0.0008;
        spot.shadow.radius = 4;
        scene.add(spot);
        scene.add(spot.target);
        spot.target.position.set(0, 0.35, 0);
        entry = { light: spot };
        lightsMap.set(l.id, entry);
      }
      const p = lightPositionFor(l.x, l.y, 4.2);
      entry.light.position.copy(p);
      entry.light.target.position.set(0, 0.35, 0);
      entry.light.target.updateMatrixWorld();
      entry.light.color.set(l.color || '#ffffff');
      entry.light.intensity = l.on ? (l.intensity / 100) * 14 : 0;
      entry.light.visible = l.on;
    }
  }, [lights]);

  // Ambient + exposure.
  useEffect(() => {
    const st = stateRef.current;
    if (!st) return;
    st.ambientLight.intensity = (ambient / 100) * 0.6 + 0.04;
    st.hemi.intensity = (ambient / 100) * 0.3 + 0.05;
    st.renderer.toneMappingExposure = 1 + exposure / 100;
  }, [ambient, exposure]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        inset: 0,
        background: '#07080a',
        overflow: 'hidden',
      }}
    />
  );
}
